import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import Select from "react-select";
import { toast } from "react-toastify";
import useAuth from "../../hooks/useAuth";
import { BtnGeneral } from "../../styles/Buttons";
import { useLocation } from "../../hooks/useLocation";
import { usePrestadores } from "../../hooks/useProvider";
import { usePatientData } from "../../context/PatientDataContext";

import {
  fetchComplemento,
  filterPrestadores,
  validateResultadoNumerico,
  saveResultado,
} from "../../api/resultApi";

const ResultModal = ({ isOpen, onClose, data, id, tipo_id, Identificacion, }) => {
  const { user } = useAuth();
  const [cons, setCons] = useState("");
  const [resultado, setResultado] = useState("");
  const [fechaResultado, setFechaResultado] = useState("");

  const [selectedDept, setSelectedDept] = useState("");
  const [selectedTown, setSelectedTown] = useState("");
  const [tipoEntidad, setTipoEntidad] = useState("");
  const [esEse, setEsEse] = useState("");

  const [showFilters, setShowFilters] = useState(false);

  const { fetchPatientData } = usePatientData();

  // ✅ Localización (solo cuando el checkbox está activo)
  const { departments, towns } = useLocation(
    showFilters ? selectedDept : null,
    showFilters
  );

  // ✅ Prestadores con lógica invertida:
  // - showFilters = false → active = true → Backend devuelve más usados
  // - showFilters = true → active = false → Backend busca por filtros
  const prestadores = usePrestadores(
    showFilters ? selectedDept : "",
    showFilters ? selectedTown : "",
    showFilters ? tipoEntidad : "",
    showFilters ? esEse : "",
    isOpen,
    showFilters ? 1 : 0
  );

  // ✅ Estado de selección y búsqueda
  const [selectedPrestador, setSelectedPrestador] = useState(null);
  const [prestadorSearch, setPrestadorSearch] = useState("");

  const [complemento, setComplemento] = useState(null);
  const [loadingComplemento, setLoadingComplemento] = useState(false);

  // ✅ Verificar si está cargando prestadores
  const isLoadingPrestadores = useMemo(() => {
    return isOpen && prestadores.length === 0;
  }, [isOpen, prestadores.length]);

  // ✅ Derivamos las options directamente de lo que llega del hook
  const prestadoresOptions = useMemo(() => {
    if (!Array.isArray(prestadores) || prestadores.length === 0) return [];

    const base = prestadorSearch.trim()
      ? filterPrestadores(prestadores, prestadorSearch)
      : prestadores;

    // Normalizamos al formato que react-select necesita
    return base.slice(0, 100).map((p) => ({
      value: p?.CODIGO_HABILITACION,
      label:
        typeof p?.RAZON_SOCIAL === "string" && p.RAZON_SOCIAL.length > 50
          ? p.RAZON_SOCIAL.substring(0, 70) + "..."
          : p?.RAZON_SOCIAL || "",
      raw: p,
      title: p?.RAZON_SOCIAL
    }));
  }, [prestadores, prestadorSearch]);

  // ✅ Al abrir la modal: reseteamos lo visual
  useEffect(() => {
    if (!isOpen) return;

    setShowFilters(false);
    setSelectedDept("");
    setSelectedTown("");
    setTipoEntidad("");
    setEsEse("");
    setSelectedPrestador(null);
    setPrestadorSearch("");
  }, [isOpen]);

  // ✅ Cuando cambian los datos del servicio (edición)
  useEffect(() => {
    if (!isOpen) return;
    if (data) {
      setCons(data.cons || "");
      setResultado(data.resultado || "");
      setFechaResultado("");
      setSelectedPrestador(null);
    }
  }, [isOpen, data]);

  // ✅ Carga del complemento (rangos)
  useEffect(() => {
    if (!isOpen || !data?.cons) return;

    const load = async () => {
      setLoadingComplemento(true);
      const resp = await fetchComplemento(data.cons, Identificacion);
      if (resp) setComplemento(resp);
      setLoadingComplemento(false);
    };

    load();
  }, [isOpen, data?.cons, Identificacion]);

  const handleSave = async () => {
    if (!resultado.trim()) return toast.warning("Ingrese el resultado");
    if (!fechaResultado.trim()) return toast.warning("Seleccione la fecha");
    if (!selectedPrestador?.value) return toast.warning("Seleccione el prestador");

    const errorRange = validateResultadoNumerico(resultado, complemento);
    if (errorRange) {
      return toast.error(
        `El valor debe estar entre ${errorRange.min} y ${errorRange.max}`
      );
    }

    const payload = {
      autoid: id,
      tipo_id,
      Identificacion,
      cons,
      resultado,
      fechaResultado,
      prestador: selectedPrestador.value,
      userId: user.id,
    };

    const res = await saveResultado(payload);
    if (res) {
      toast.success("Resultado guardado correctamente");
      fetchPatientData();
      onClose();
    }
  };

  const renderCampoResultado = () => {
    if (loadingComplemento) {
      return (
        <input
          type="text"
          disabled
          placeholder="Cargando..."
          className="input-field"
        />
      );
    }

    // Si no hay complemento → input de texto normal
    if (!complemento) {
      return (
        <>
          <div className="form-row">
            <label className="form-label">Fecha resultado:</label>
            <input
              type="date"
              value={fechaResultado}
              onChange={(e) => setFechaResultado(e.target.value)}
              className="input-field"
            />
            <label className="form-label"></label>
            <label className="form-label"></label>
            <label className="form-label"></label>
          </div>

          <div className="form-row">
            <label className="form-label">Observación:</label>
            <textarea
              value={resultado}
              onChange={(e) => setResultado(e.target.value)}
              className="input-field"
            />
          </div>
        </>
      );
    }

    // Datos del complemento
    const cod = complemento.codificacion || [];
    const valores = complemento.valores_normales?.[0] || null;

    // Detectar si es lista
    const lista = cod
      .filter(
        (item) =>
          item.IdLista !== null &&
          item.IdLista !== "" &&
          item.Valor !== null &&
          item.Valor !== ""
      )
      .map((item) => ({
        value: String(item.IdLista),
        label: item.Valor,
      }));

    const unidad = cod?.[0]?.unidades || "";

    // ✔ Campo tipo LISTA
    if (lista.length > 0) {
      return (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <label className="form-label">Resultado:</label>
            <select
              value={resultado}
              onChange={(e) => setResultado(e.target.value)}
              className="input-field"
            >
              <option value="">Seleccione</option>
              {lista.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <label className="form-label">Fecha resultado:</label>
            <input
              type="date"
              value={fechaResultado}
              onChange={(e) => setFechaResultado(e.target.value)}
              className="input-field"
            />
          </div>
        </>
      );
    }

    // ✔ Campo tipo NUMÉRICO con rango
    if (
      valores &&
      valores.Valor_Minimo !== null &&
      valores.Valor_maximo !== null
    ) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <label className="form-label">Resultado:</label>
          <input
            type="number"
            step="0.01"
            value={resultado}
            min={valores.Valor_Minimo}
            max={valores.Valor_maximo}
            onChange={(e) => setResultado(e.target.value)}
            className="input-field"
          />
          {unidad && <span className="unidad">{unidad}</span>}
          <label className="form-label">Fecha resultado:</label>
          <input
            type="date"
            value={fechaResultado}
            onChange={(e) => setFechaResultado(e.target.value)}
            className="input-field"
          />
        </div>
      );
    }

    // ✔ Campo texto + unidad
    return (
      <>
          <div className="form-row">
            <label className="form-label">Fecha resultado:</label>
            <input
              type="date"
              value={fechaResultado}
              onChange={(e) => setFechaResultado(e.target.value)}
              className="input-field"
            />
            <label className="form-label"></label>
            <label className="form-label"></label>
            <label className="form-label"></label>
          </div>

          <div className="form-row">
            <label className="form-label">Resultado:</label>
            <textarea
              value={resultado}
              onChange={(e) => setResultado(e.target.value)}
              className="input-field"
            />
          </div>
        </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Detalle del Resultado - ${data?.desc}`}
      width="750px"
    >
      <div className="form-container">
        {renderCampoResultado()}

        <h6
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <b>Datos del Prestador</b>

          <label style={{ fontSize: "13px", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={showFilters}
              onChange={() => {
                const newShowFilters = !showFilters;                
                setShowFilters(newShowFilters);
                setSelectedDept("");
                setSelectedTown("");
                setTipoEntidad("");
                setEsEse("");
              }}
              style={{ marginRight: "6px" }}
            />
            Mostrar filtros
          </label>
        </h6>

        {showFilters && (
          <>
            <div className="form-row">
              <label className="form-label">Departamento:</label>
              <select
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
                className="input-field"
              >
                <option value="">Seleccione</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.descripcion}
                  </option>
                ))}
              </select>

              <label className="form-label">Municipio:</label>
              <select
                value={selectedTown}
                onChange={(e) => setSelectedTown(e.target.value)}
                className="input-field"
                disabled={!selectedDept}
              >
                <option value="">Seleccione</option>
                {towns.map((t) => (
                  <option key={t.codigo} value={t.codigo}>
                    {t.descripcion}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label className="form-label">Tipo:</label>
              <select
                value={tipoEntidad}
                onChange={(e) => setTipoEntidad(e.target.value)}
                className="input-field"
              >
                <option value="">Seleccione</option>
                <option value="4">Pública</option>
                <option value="1">Privada</option>
                <option value="3">Mixta</option>
              </select>

              <label className="form-label">ESE:</label>
              <select
                value={esEse}
                onChange={(e) => setEsEse(e.target.value)}
                className="input-field"
              >
                <option value="">Seleccione</option>
                <option value="SI">Sí</option>
                <option value="NO">No</option>
                <option value="N/A">N/A</option>
              </select>
            </div>
          </>
        )}

        <div className="form-row">
          <label className="form-label">Prestador:</label>

          {isLoadingPrestadores && (
            <div className="input-field" style={{ color: "#999" }}>
              Cargando prestadores...
            </div>
          )}

          {!isLoadingPrestadores && isOpen && prestadores.length > 0 && (
            <Select
              className="input-field-select"
              value={selectedPrestador}
              onChange={setSelectedPrestador}
              options={prestadoresOptions}
              placeholder="Buscar prestador..."
              isClearable
              isSearchable
              onInputChange={(txt) => setPrestadorSearch(txt || "")}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 999999 }),
                menu: base => ({ ...base, zIndex: 999999 })
              }}
              noOptionsMessage={() => "No se encontraron resultados"}
            />
          )}
        </div>
      </div>

      <div className="actions">
        <BtnGeneral
          bg="green"
          padding="8px"
          radius="8px"
          w="120px"
          onClick={handleSave}
        >
          Guardar
        </BtnGeneral>
      </div>

      <style>
        {`
          .form-container {
            display: flex;
            flex-direction: column;
            gap: 18px;
          }
          .form-row {
            display: flex;
            align-items: left;
            gap: 12px;
          }
          .form-label {
            width: 120px;
            text-align: left;
            font-size: 14px;
            color: #444;
            font-weight: 500;
          }
          .input-field {
            flex: 1;
            padding: 8px 10px;
            border: 1px solid #ccc;
            border-radius: 6px;
            font-size: 14px;
            transition: all 0.2s ease;
            min-width: 150px;
          }
          .input-field:focus {
            outline: none;
            border-color: #008c5a;
            box-shadow: 0 0 4px rgba(0, 140, 90, 0.3);
          }
          .input-field-select {
            flex: 1;
            border: 1px solid #ccc;
            border-radius: 6px;
            font-size: 14px;
            transition: all 0.2s ease;
            min-width: 150px;
          }
          .actions {
            display: flex;
            justify-content: flex-end;
            margin-top: 20px;
          }
        `}
      </style>
    </Modal>
  );
};

export default ResultModal;
